// ─────────────────────────────────────────────
//  Facilities domain — known facilities for cross-facility staffing (D-09).
//
//  To add a facility: append one entry to KNOWN_FACILITIES.
//  id must match the facilityId stored in user.activeFacilityId and
//  procedure_refs/{facilityId} in Firestore.
//
//  MULTI-TENANT NOTE: KNOWN_FACILITIES is a SEED for this deployment, not a
//  source of truth. A different customer's project has entirely different
//  campuses, so anything that must work for them reads the live `facilities`
//  docs and treats this list as a fallback of last resort.
// ─────────────────────────────────────────────
import { DEFAULT_FACILITY_ID } from "../config/roles";
// The lifecycle rule lives in facilityRules and is imported, not re-stated —
// see isFacilityActive at the bottom of this file for why that matters.
// facilityRules is a leaf module (no imports of its own), so this cannot cycle.
import { resolveFacilityStatus } from "./facilityRules";
// testSession is a LEAF (it imports nothing), so this cannot cycle — the same
// argument that lets facilityRules be imported above.
import { isTestFacility } from "./testSession";

// shortTag is an EDITORIAL abbreviation for tight UI (the TV board's call-list
// badge), not derived from the label — optional; a facility with none keeps
// its full label wherever a short tag is requested (see facilityShortTag).
export const KNOWN_FACILITIES = [
  { id: "default",     label: "AdventHealth Wesley Chapel", shortTag: "WC" },
  { id: "carrollwood", label: "AdventHealth Carrollwood",   shortTag: "CAR" },
  { id: "tampa",       label: "AdventHealth Tampa" },
];

// All facilities except the current one — the list the picker offers.
export function otherFacilities(currentId) {
  return KNOWN_FACILITIES.filter((f) => f.id !== currentId);
}

// Human-readable label for a facility id. Prefers the LIVE facility doc's name
// when the caller has the docs (a tenant renames its own campuses; the built-in
// list is only a seed), then the built-in label, then the raw id.
export function facilityLabel(id, facilityDocs) {
  if (Array.isArray(facilityDocs)) {
    const live = facilityDocs.find((f) => f && f.id === id);
    const name = live?.name || live?.label;
    if (name) return name;
  }
  return KNOWN_FACILITIES.find((f) => f.id === id)?.label || id;
}

// The org name every facility label starts with — "AdventHealth Tampa" +
// "AdventHealth Carrollwood" → "AdventHealth". DERIVED, not hardcoded: a
// second tenant's campuses share their own operator's name, and hardcoding
// ours would leave their topbar showing the full string while ours shortened.
// Whole words only, so "St. Mary's North"/"St. Mary's South" yields "St.
// Mary's" and never a partial word. Needs at least two labels to be sure a
// shared word is an org name and not part of a single campus's own name.
export function commonOrgPrefix(labels) {
  const wordLists = (labels || [])
    .map((l) => String(l || "").trim())
    .filter(Boolean)
    .map((l) => l.split(/\s+/));
  if (wordLists.length < 2) return "";
  const shared = [];
  for (let i = 0; i < wordLists[0].length; i += 1) {
    const word = wordLists[0][i];
    if (!wordLists.every((w) => w[i] === word)) break;
    shared.push(word);
  }
  // All labels identical → the "shared prefix" is the whole name; stripping it
  // would leave an empty tag, so treat that as no prefix.
  if (shared.length === wordLists[0].length) return "";
  return shared.join(" ");
}

// Short display form for tight UI (topbar tag): drops the operator prefix the
// campuses share — "AdventHealth Tampa" → "Tampa". Pass the tenant's live
// labels to derive their prefix; omitting them falls back to the built-in
// list, which keeps today's AdventHealth behavior unchanged.
export function shortFacilityLabel(fullName, labels) {
  const name = String(fullName || "");
  const prefix = commonOrgPrefix(labels || KNOWN_FACILITIES.map((f) => f.label));
  if (!prefix) return name;
  const re = new RegExp(`^${prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s+`, "i");
  const short = name.replace(re, "");
  // Never shorten a name into nothing — a blank facility tag is worse than a
  // long one.
  return short || name;
}

// Compact abbreviation for tight UI (e.g. the TV board's call-list badge) —
// "WC" for Wesley Chapel, "CAR" for Carrollwood. This is an EDITORIAL choice
// per campus, not something derivable from the name string: there is no
// general rule that turns "Carrollwood" into "CAR" but leaves "Tampa" whole.
// So, like facilityLabel, it prefers the tenant's OWN live facility doc field
// first, then the built-in seed's tag, then null — the caller shows the full,
// unshortened label rather than guessing an abbreviation from a name it has
// never seen before.
export function facilityShortTag(id, facilityDocs) {
  if (Array.isArray(facilityDocs)) {
    const live = facilityDocs.find((f) => f && f.id === id);
    if (live?.shortTag) return live.shortTag;
  }
  return KNOWN_FACILITIES.find((f) => f.id === id)?.shortTag || null;
}

// The three AdventHealth campuses that are wired into code today (global-sheet
// parsing, cross-board sync). Used as the RESILIENT FALLBACK when the live
// facilities collection can't be read — never let cross-board sync break just
// because a read failed. New facilities extend the live list, not this.
export const CORE_FACILITY_IDS = KNOWN_FACILITIES.map((f) => f.id);

// Active facility ids derived from live `facilities` docs, so cross-board
// features (MD-call sync, cross-campus AIC colouring) automatically cover a
// newly-added campus instead of a hardcoded 3-id array. `default` is always
// included (the core home facility is active even before its doc is written).
// Falls back to CORE_FACILITY_IDS when the docs are missing/empty so callers
// never get an empty list. Order: default first, then the rest as given.
export function activeFacilityIds(facilityDocs, homeFacilityId = DEFAULT_FACILITY_ID) {
  // No docs at all = we couldn't read the collection → safe fallback to the
  // known cores so cross-board sync keeps working exactly as before.
  if (!Array.isArray(facilityDocs) || facilityDocs.length === 0) {
    return [...CORE_FACILITY_IDS];
  }
  const active = facilityDocs
    .filter(isFacilityActive)
    .map((f) => f.id)
    .filter(Boolean);
  // The HOME facility leads the list and is always present — it is the tenant's
  // primary campus and counts as active even before its doc is written, so
  // cross-board sync never loses it to a missing or unreadable doc.
  //
  // What changed for multi-tenancy is WHICH id that is: this used to inject the
  // literal "default", so a tenant whose campuses are mercy-main and
  // mercy-north carried a phantom facility through every cross-board feature
  // (MD-call sync, cross-campus AIC colouring) that resolves to nothing.
  const lead = homeFacilityId ? [homeFacilityId] : [];
  return [...new Set([...lead, ...active])];
}

// Is a facility doc "Active"? ONE definition, shared with the status chip an
// administrator reads: this is exactly `resolveFacilityStatus(f) === "active"`.
// The boolean gate and the label are now the same sentence, so they cannot
// disagree about a campus.
//
// ⚠️ THIS USED TO BE A SECOND, HAND-WRITTEN COPY of the rule:
//       f.facilityStatus === "active" || (f.facilityStatus == null && f.isActive === true)
// A copied predicate is a predicate that will diverge, and this pair had: the
// copy here demanded positive proof of activity, while resolveFacilityStatus
// defaulted everything it did not recognise to "active". So a facility could be
// excluded from the nightly import and cross-board sync while Setup, the nav
// menu and every cross-facility picker showed it as Active — and a doc written
// `facilityStatus: "inactive"` read as ACTIVE in the UI.
//
// ⚠️ Deleting the copy is the whole fix; the behaviour of THIS function is
// deliberately UNCHANGED for every input. The drift was resolved in the other
// direction, by tightening resolveFacilityStatus up to match the strict rule
// that was already here — because this is the predicate guarding the
// destructive path (activeFacilityIds → functions/dailyImport), and loosening
// it to match the lenient one would have enrolled real campuses in a board
// rewrite. See resolveFacilityStatus for the fail-closed argument.
export function isFacilityActive(f) {
  // ⚠️ A SHARED TEST SESSION IS NEVER ACTIVE, AND THIS IS THE LOCK THAT MATTERS.
  // A session is a real facility (`tst-<code>`) so that every listener, rule and
  // push works on it unchanged — which also means it would be returned by
  // `activeFacilityIds()`, and that list is what `functions/dailyImport`
  // iterates before FULL-RESETTING each board it is given. A rehearsal left in
  // that list gets rewritten at 1 AM, and the same gap in reverse is how a
  // rehearsal could be pointed at a live OR board.
  //
  // Checked FIRST, before the lifecycle rule, so it holds whatever the doc's
  // status field says — including a hand-edited one. The session doc is also
  // written `facilityStatus: "inactive"`, which reaches the same answer through
  // resolveFacilityStatus; two independent reasons, so removing either one does
  // not open the destructive path.
  if (isTestFacility(f)) return false;
  return resolveFacilityStatus(f) === "active";
}
